import { useState, useEffect } from 'react';
import { 
  Bell,
  BellOff,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  LogOut,
  Send,
  Search,
  Trash2,
  Copy,
  ExternalLink,
  AlertCircle,
  Shield,
  ChevronDown,
  ChevronUp,
  MapPin,
  Pill,
  Activity,
  Users,
  Power,
  PowerOff,
  Loader2,
  Calendar,
  Utensils,
  Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PASSWORD = 'CuraCheck#Secure2025!Xk9';

// Piano badge colors
const PLAN_STYLES = {
  premium: 'bg-amber-100 text-amber-700 border-amber-200',
  standard: 'bg-blue-100 text-blue-700 border-blue-200',
  fondatori: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  founder: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  trial: 'bg-gray-100 text-gray-600 border-gray-200',
};

const PLAN_OPTIONS = ['trial', 'fondatori', 'standard', 'premium'];

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [families, setFamilies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFamily, setExpandedFamily] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [loadingDiagnostic, setLoadingDiagnostic] = useState(false);
  const [sendingTest, setSendingTest] = useState(null);
  const [togglingStatus, setTogglingStatus] = useState(null);
  const [changingPlan, setChangingPlan] = useState(null);

  // Login
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
      loadFamilies();
    } else {
      setError('Password non valida');
    }
  };

  // Load families
  const loadFamilies = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/families?password=${encodeURIComponent(ADMIN_PASSWORD)}`);
      if (res.ok) {
        const data = await res.json();
        setFamilies(data.families || []);
      }
    } catch (err) {
      console.error('Load error:', err);
      toast.error('Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  // Load diagnostic for a family
  const loadDiagnostic = async (familyId) => {
    setLoadingDiagnostic(true);
    setDiagnostic(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/families/${familyId}/diagnostic?password=${encodeURIComponent(ADMIN_PASSWORD)}`);
      if (res.ok) {
        const data = await res.json();
        setDiagnostic(data);
      } else {
        toast.error('Errore caricamento diagnostica');
      }
    } catch (err) {
      console.error('Diagnostic error:', err);
      toast.error('Errore caricamento diagnostica');
    } finally {
      setLoadingDiagnostic(false);
    }
  };

  // Toggle family expand
  const toggleExpand = (familyId) => {
    if (expandedFamily === familyId) {
      setExpandedFamily(null);
      setDiagnostic(null);
    } else {
      setExpandedFamily(familyId);
      loadDiagnostic(familyId);
    }
  };

  // Filter families
  const filteredFamilies = families.filter(f => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      f.user_email?.toLowerCase().includes(q) ||
      f.nome_famiglia?.toLowerCase().includes(q) ||
      f.nome_assistito?.toLowerCase().includes(q) ||
      f.family_code?.toLowerCase().includes(q)
    );
  });

  // Test push notification
  const sendTestPush = async (famigliaId) => {
    setSendingTest(famigliaId);
    try {
      const res = await fetch(`${API_URL}/api/notifications/test?famiglia_id=${famigliaId}`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Notifica test inviata!');
      } else {
        toast.error(data.error || 'Errore invio');
      }
    } catch (err) {
      toast.error('Errore invio notifica');
    } finally {
      setSendingTest(null);
    }
  };

  // Toggle account status
  const toggleStatus = async (familyId) => {
    setTogglingStatus(familyId);
    try {
      const res = await fetch(`${API_URL}/api/admin/families/${familyId}/toggle-status?password=${encodeURIComponent(ADMIN_PASSWORD)}`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.active ? 'Account attivato' : 'Account disattivato');
        loadFamilies();
        if (expandedFamily === familyId) {
          loadDiagnostic(familyId);
        }
      } else {
        toast.error('Errore cambio stato');
      }
    } catch (err) {
      toast.error('Errore cambio stato');
    } finally {
      setTogglingStatus(null);
    }
  };

  // Change plan
  const changePlan = async (familyId, newPlan) => {
    setChangingPlan(familyId);
    try {
      const res = await fetch(`${API_URL}/api/admin/families/${familyId}/change-plan?password=${encodeURIComponent(ADMIN_PASSWORD)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ piano: newPlan })
      });
      if (res.ok) {
        toast.success(`Piano cambiato a ${newPlan}`);
        loadFamilies();
        if (expandedFamily === familyId) {
          loadDiagnostic(familyId);
        }
      } else {
        toast.error('Errore cambio piano');
      }
    } catch (err) {
      toast.error('Errore cambio piano');
    } finally {
      setChangingPlan(null);
    }
  };

  // Delete family
  const deleteFamily = async (familyId, familyName) => {
    if (!window.confirm(`Eliminare "${familyName}"?\n\nTutti i dati verranno cancellati permanentemente.`)) {
      return;
    }
    
    try {
      const res = await fetch(
        `${API_URL}/api/admin/families/${familyId}?password=${encodeURIComponent(ADMIN_PASSWORD)}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        toast.success('Famiglia eliminata');
        setExpandedFamily(null);
        setDiagnostic(null);
        loadFamilies();
      } else {
        toast.error('Errore eliminazione');
      }
    } catch (err) {
      toast.error('Errore eliminazione');
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiato!`);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get quick health status
  const getQuickHealth = (family) => {
    const issues = [];
    if (!family.onesignal_player_id) issues.push('push');
    if (family.trial_expired) issues.push('expired');
    if (!family.setup_complete) issues.push('setup');
    if (!family.active) issues.push('disabled');
    return issues;
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#4A7C59] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Centro Operativo</h1>
            <p className="text-gray-500 mt-1">CuraCheck Admin</p>
          </div>
          
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password admin"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent outline-none"
              autoFocus
            />
            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
            <Button type="submit" className="w-full mt-4 bg-[#4A7C59] hover:bg-[#3d6549] h-12">
              Accedi
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#4A7C59] rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900">Centro Operativo</h1>
                <p className="text-xs text-gray-500">{families.length} famiglie</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={loadFamilies} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setIsAuthenticated(false)}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Search */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca per email, nome famiglia, codice..."
            className="w-full pl-12 pr-4 py-4 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent outline-none text-lg"
          />
        </div>
      </div>

      {/* Families List */}
      <div className="max-w-6xl mx-auto px-4 pb-8">
        <div className="space-y-3">
          {filteredFamilies.map((family, index) => {
            const isExpanded = expandedFamily === family.id;
            const healthIssues = getQuickHealth(family);
            const hasCritical = healthIssues.includes('expired') || healthIssues.includes('disabled');
            const hasWarning = healthIssues.length > 0;
            const familyNumber = index + 1;
            const shortId = family.id?.substring(0, 8);
            
            return (
              <div 
                key={family.id}
                className={`bg-white rounded-2xl border-2 transition-all overflow-hidden ${
                  hasCritical ? 'border-red-200' : 
                  hasWarning ? 'border-amber-200' : 
                  'border-gray-100'
                }`}
              >
                {/* Main Row */}
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleExpand(family.id)}
                >
                  <div className="flex items-center gap-4">
                    {/* Number badge */}
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-gray-600">#{familyNumber}</span>
                    </div>
                    
                    {/* Status indicator */}
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                      hasCritical ? 'bg-red-500' :
                      hasWarning ? 'bg-amber-500' :
                      'bg-emerald-500'
                    }`} />
                    
                    {/* Main info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 truncate">
                          {family.user_email || 'Email non disponibile'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${PLAN_STYLES[family.piano] || PLAN_STYLES.trial}`}>
                          {family.piano || 'trial'}
                        </span>
                        {!family.active && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                            DISATTIVATO
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        {family.nome_famiglia || family.nome_assistito || '-'}
                        {family.family_code && (
                          <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-mono">
                            {family.family_code}
                          </span>
                        )}
                        <span className="ml-2 text-gray-400 text-xs font-mono">
                          ID: {shortId}
                        </span>
                      </p>
                    </div>
                    
                    {/* Quick icons */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {family.onesignal_player_id ? (
                        <Bell className="w-4 h-4 text-emerald-500" title="Push attive" />
                      ) : (
                        <BellOff className="w-4 h-4 text-gray-300" title="Push non attive" />
                      )}
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Expanded Diagnostic Panel */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50/50">
                    {loadingDiagnostic ? (
                      <div className="p-8 text-center">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                        <p className="text-sm text-gray-500 mt-2">Caricamento diagnostica...</p>
                      </div>
                    ) : diagnostic ? (
                      <div className="p-4 space-y-4">
                        {/* Issues Alert */}
                        {diagnostic.issues?.length > 0 && (
                          <div className="bg-white rounded-xl border border-red-200 p-4">
                            <h4 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                              <AlertCircle className="w-4 h-4" />
                              Problemi Rilevati ({diagnostic.issues.length})
                            </h4>
                            <div className="space-y-2">
                              {diagnostic.issues.map((issue, i) => (
                                <div 
                                  key={i}
                                  className={`flex items-start gap-2 text-sm p-2 rounded-lg ${
                                    issue.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
                                  }`}
                                >
                                  {issue.type === 'error' ? (
                                    <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                  ) : (
                                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                  )}
                                  <div>
                                    <span className="font-medium">{issue.category}:</span> {issue.message}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Account Info Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <InfoCard label="ID Famiglia" value={diagnostic.famiglia_id?.substring(0, 8) + '...'} copyValue={diagnostic.famiglia_id} onCopy={copyToClipboard} />
                          <InfoCard label="Registrato" value={formatDate(diagnostic.account?.created_at)} />
                          <InfoCard label="Assistito" value={diagnostic.account?.nome_assistito || '-'} />
                          <InfoCard label="Assistente" value={diagnostic.account?.nome_assistente || '-'} />
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <InfoCard 
                            label="Setup" 
                            value={diagnostic.account?.setup_complete ? 'Completato' : 'Incompleto'}
                            status={diagnostic.account?.setup_complete ? 'success' : 'warning'}
                          />
                          <InfoCard label="Family Code" value={diagnostic.account?.family_code || 'Non generato'} copyValue={diagnostic.account?.family_code} onCopy={copyToClipboard} />
                          <InfoCard label="Token" value={diagnostic.account?.token?.substring(0, 10) + '...' || '-'} copyValue={diagnostic.account?.token} onCopy={copyToClipboard} />
                          <InfoCard label="Piano" value={diagnostic.account?.piano || 'trial'} />
                        </div>

                        {/* Services Status */}
                        <div className="bg-white rounded-xl border border-gray-200 p-4">
                          <h4 className="font-semibold text-gray-700 mb-3">Stato Servizi</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <ServiceStatus 
                              icon={Bell}
                              label="Push Notifications"
                              active={diagnostic.push_notifications?.enabled}
                            />
                            <ServiceStatus 
                              icon={Pill}
                              label="Medicine"
                              active={diagnostic.medicine_schedules?.length > 0}
                              count={diagnostic.medicine_schedules?.length}
                            />
                            <ServiceStatus 
                              icon={Activity}
                              label="Attività Recenti"
                              active={diagnostic.recent_activities?.length > 0}
                              count={diagnostic.recent_activities?.length}
                            />
                            <ServiceStatus 
                              icon={MapPin}
                              label="GPS Sessions"
                              active={diagnostic.gps_sessions?.length > 0}
                              count={diagnostic.gps_sessions?.length}
                            />
                          </div>
                        </div>

                        {/* Recent Activities */}
                        {diagnostic.recent_activities?.length > 0 && (
                          <div className="bg-white rounded-xl border border-gray-200 p-4">
                            <h4 className="font-semibold text-gray-700 mb-3">Ultime Attività</h4>
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                              {diagnostic.recent_activities.slice(0, 5).map((act, i) => (
                                <div key={i} className="flex items-center gap-3 text-sm p-2 bg-gray-50 rounded-lg">
                                  <Activity className="w-4 h-4 text-gray-400" />
                                  <span className="font-medium text-gray-700">{act.tipo}</span>
                                  <span className="text-gray-500 truncate flex-1">{act.descrizione}</span>
                                  <span className="text-xs text-gray-400">{formatDate(act.created_at)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Medicine Schedules */}
                        {diagnostic.medicine_schedules?.length > 0 && (
                          <div className="bg-white rounded-xl border border-gray-200 p-4">
                            <h4 className="font-semibold text-gray-700 mb-3">Medicine Schedulate</h4>
                            <div className="flex flex-wrap gap-2">
                              {diagnostic.medicine_schedules.map((med, i) => (
                                <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                                  {med.nome_farmaco} - {med.orario}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Admin Actions */}
                        <div className="bg-white rounded-xl border border-gray-200 p-4">
                          <h4 className="font-semibold text-gray-700 mb-3">Azioni Admin</h4>
                          
                          {/* Change Plan */}
                          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
                            <span className="text-sm text-gray-600 w-24">Cambia Piano:</span>
                            <div className="flex flex-wrap gap-2">
                              {PLAN_OPTIONS.map(plan => (
                                <button
                                  key={plan}
                                  onClick={() => changePlan(family.id, plan)}
                                  disabled={changingPlan === family.id || family.piano === plan}
                                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                                    family.piano === plan
                                      ? 'bg-[#4A7C59] text-white border-[#4A7C59]'
                                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#4A7C59] hover:text-[#4A7C59]'
                                  } disabled:opacity-50`}
                                >
                                  {plan}
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2">
                            {family.token && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => copyToClipboard(`${window.location.origin}/app/${family.token}`, 'Link assistente')}
                                >
                                  <Copy className="w-4 h-4 mr-1" />
                                  Copia Link
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.open(`/app/${family.token}`, '_blank')}
                                >
                                  <ExternalLink className="w-4 h-4 mr-1" />
                                  Apri App
                                </Button>
                              </>
                            )}
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => sendTestPush(family.id)}
                              disabled={!family.onesignal_player_id || sendingTest === family.id}
                            >
                              <Send className={`w-4 h-4 mr-1 ${sendingTest === family.id ? 'animate-pulse' : ''}`} />
                              Test Push
                            </Button>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleStatus(family.id)}
                              disabled={togglingStatus === family.id}
                              className={family.active ? 'text-amber-600 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'}
                            >
                              {family.active ? (
                                <>
                                  <PowerOff className="w-4 h-4 mr-1" />
                                  Disattiva
                                </>
                              ) : (
                                <>
                                  <Power className="w-4 h-4 mr-1" />
                                  Riattiva
                                </>
                              )}
                            </Button>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50 border-red-200"
                              onClick={() => deleteFamily(family.id, family.nome_famiglia || family.user_email)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Elimina
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        Errore caricamento diagnostica
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          
          {filteredFamilies.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              {searchQuery ? 'Nessun risultato trovato' : 'Nessuna famiglia registrata'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Info Card Component
const InfoCard = ({ label, value, status, copyValue, onCopy }) => (
  <div className="bg-white p-3 rounded-xl border border-gray-200">
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <div className="flex items-center justify-between">
      <p className={`font-medium truncate ${
        status === 'success' ? 'text-emerald-600' : 
        status === 'warning' ? 'text-amber-600' : 
        status === 'error' ? 'text-red-600' : 
        'text-gray-900'
      }`}>
        {value}
      </p>
      {copyValue && onCopy && (
        <button 
          onClick={(e) => { e.stopPropagation(); onCopy(copyValue, label); }}
          className="ml-2 p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
          title={`Copia ${label}`}
        >
          <Copy className="w-3 h-3" />
        </button>
      )}
    </div>
  </div>
);

// Service Status Component
const ServiceStatus = ({ icon: Icon, label, active, count }) => (
  <div className={`p-3 rounded-xl border ${active ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
    <div className="flex items-center gap-2">
      <Icon className={`w-4 h-4 ${active ? 'text-emerald-600' : 'text-gray-400'}`} />
      <span className={`text-sm font-medium ${active ? 'text-emerald-700' : 'text-gray-500'}`}>
        {label}
      </span>
    </div>
    {count !== undefined && (
      <p className={`text-lg font-bold mt-1 ${active ? 'text-emerald-600' : 'text-gray-400'}`}>
        {count}
      </p>
    )}
  </div>
);

export default AdminDashboard;
